import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serversApi, Server, ServerCreate, ServerUpdate, ListServersParams } from '../lib/api/servers';

export function useServers(params: ListServersParams = {}) {
  return useQuery({
    queryKey: ['servers', params],
    queryFn: () => serversApi.list(params),
  });
}

export function useServer(id: string) {
  return useQuery({
    queryKey: ['server', id],
    queryFn: () => serversApi.get(id),
    enabled: !!id,
  });
}

export function useCreateServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ServerCreate) => serversApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
    },
  });
}

export function useUpdateServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ServerUpdate }) =>
      serversApi.update(id, data),
    onSuccess: (server) => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.setQueryData(['server', server.id], server);
    },
  });
}

export function useDeleteServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => serversApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
    },
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (id: string) => serversApi.testConnection(id),
  });
}

export function useServerStats(id: string) {
  return useQuery({
    queryKey: ['server-stats', id],
    queryFn: () => serversApi.getStats(id),
    enabled: !!id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useServerContainers(id: string) {
  return useQuery({
    queryKey: ['server-containers', id],
    queryFn: () => serversApi.getContainers(id),
    enabled: !!id,
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

export function useContainerLogs(serverId: string, containerId: string, tail: number = 100) {
  return useQuery({
    queryKey: ['container-logs', serverId, containerId, tail],
    queryFn: () => serversApi.getContainerLogs(serverId, containerId, tail),
    enabled: !!serverId && !!containerId,
  });
}

export function useContainerAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serverId, containerId, action }: {
      serverId: string;
      containerId: string;
      action: 'start' | 'stop' | 'restart'
    }) => serversApi.containerAction(serverId, containerId, action),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['server-containers', variables.serverId] });
    },
  });
}
